import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { httpResource, HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, map, of } from 'rxjs';
import {
  ButtonSize,
  MibBreakpointService,
  MibButtonDirective,
  MibIconComponent,
  MibMenuComponent,
  MibMenuEntry,
  MibMenuTriggerDirective,
  MibSpinnerComponent,
  MibToastService,
} from '@medinbox/angular';
import { LiveSessionApiService, RoomsApiService } from '@core/services';
import { LIVE_SESSION_CONFLICT_STATUS, Room } from '@shared/models';
import { extractApiErrorMessage } from '@shared/utils';

/**
 * Starts a live: a button when one room is free, a menu when several are; busy rooms stay disabled.
 * Availability is only a hint: a room taken meanwhile comes back as the `409` handled below.
 */
@Component({
  selector: 'mai-new-live-button',
  imports: [
    MibButtonDirective,
    MibIconComponent,
    MibMenuComponent,
    MibMenuTriggerDirective,
    MibSpinnerComponent,
  ],
  templateUrl: './new-live-button.html',
  host: { class: 'contents' },
})
export class NewLiveButton {
  private readonly roomsApi = inject(RoomsApiService);
  private readonly liveSessionApi = inject(LiveSessionApiService);
  private readonly toastService = inject(MibToastService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly breakpointService = inject(MibBreakpointService);
  protected readonly notExpanded = computed(
    () => this.breakpointService.sizeClass() !== 'expanded',
  );

  /** Height of the button, so it can sit level with whatever bar hosts it. */
  readonly size = input<ButtonSize>('md');

  private readonly roomsResource = httpResource<Room[]>(() => this.roomsApi.endpoint);
  private readonly rooms = computed(() => this.roomsResource.value() ?? []);

  /** Rooms whose `current-session` answered with a live. Empty until the check has come back. */
  private readonly busyRooms = signal<ReadonlySet<string>>(new Set());
  /** The room a start is in flight on, `null` when none — the button's own pending state. */
  private readonly startingRoom = signal<string | null>(null);

  protected readonly pending = computed(() => this.startingRoom() !== null);
  private readonly availableRooms = computed(() =>
    this.rooms().filter((room) => !this.busyRooms().has(room.slug)),
  );

  /** Hidden, not disabled, until the organization is known to have a room. */
  protected readonly visible = computed(() => this.rooms().length > 0);
  /** Several rooms to choose from: the click opens the menu instead of starting anything. */
  protected readonly menuMode = computed(() => this.availableRooms().length > 1);
  protected readonly canStart = computed(() => this.availableRooms().length > 0);

  /**
   * Every room, the busy ones disabled and told why. The order is the backend's, so the menu reads
   * the same way as the administration list.
   */
  protected readonly menuItems = computed<MibMenuEntry[]>(() =>
    this.rooms().map((room) => {
      const busy = this.busyRooms().has(room.slug);
      return {
        label: room.label,
        value: room.slug,
        icon: 'video' as const,
        disabled: busy || this.pending(),
        tooltip: busy ? 'A live is already running in this room.' : undefined,
      };
    }),
  );

  /** The visible label, when any, is only "New live": the pending or disabled reason lives here. */
  protected readonly accessibleLabel = computed(() => {
    if (this.pending()) {
      return 'New live — starting';
    }
    return this.canStart() ? 'New live' : 'New live — a live is already running in every room';
  });

  constructor() {
    // `untracked`: `busyRooms` is written in the check, and tracking it would re-run this effect.
    effect(() => {
      const rooms = this.rooms();
      untracked(() => this.refreshAvailability(rooms));
    });
  }

  /** Starts the one free room. The button only offers this when there is exactly one. */
  protected startFirstAvailable(): void {
    const room = this.availableRooms()[0];
    if (room) {
      this.startLive(room.slug);
    }
  }

  /** On `204` or `409` the room is marked busy at once, so a second click cannot ask for it again. */
  protected startLive(slug: string): void {
    if (this.pending()) {
      return;
    }

    const label = this.rooms().find((room) => room.slug === slug)?.label ?? slug;
    this.startingRoom.set(slug);

    this.liveSessionApi
      .startSession(slug)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.startingRoom.set(null);
          this.markBusy(slug);
          this.toastService.show({
            message: `Live started in "${label}".`,
            description: 'The case is being recorded.',
            type: 'success',
          });
        },
        error: (error: unknown) => {
          this.startingRoom.set(null);

          if (error instanceof HttpErrorResponse && error.status === LIVE_SESSION_CONFLICT_STATUS) {
            this.markBusy(slug);
            this.toastService.show({
              message: `A live is already running in "${label}".`,
              description: 'Nothing was created. Pick another room, or stop that live first.',
              type: 'warning',
            });
            return;
          }

          this.toastService.show({
            message: `The live could not be started in "${label}".`,
            description: extractApiErrorMessage(error, 'Please try again.'),
            type: 'error',
          });
        },
      });
  }

  /** A failed check counts the room as free: the start's `409` is the authority on busy. */
  private refreshAvailability(rooms: readonly Room[]): void {
    if (!rooms.length) {
      this.busyRooms.set(new Set());
      return;
    }

    forkJoin(
      rooms.map((room) =>
        this.liveSessionApi.currentSession(room.slug).pipe(
          map((session) => session !== null),
          catchError(() => of(false)),
          map((busy) => [room.slug, busy] as const),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((entries) => {
        this.busyRooms.set(new Set(entries.filter(([, busy]) => busy).map(([roomId]) => roomId)));
      });
  }

  /** Records locally what the backend has just told us, without re-reading every room for it. */
  private markBusy(roomId: string): void {
    this.busyRooms.update((busy) => new Set(busy).add(roomId));
  }
}
